import Link from '@/routing/Link'
import { useTranslation } from 'react-i18next'
import { isContextMaterialIndexingInProgress } from '@digital-twins/shared-types'
import { Text } from '@/components/ui/Text'
import { KnowledgeDocumentWithStages } from '@/lib/api'
import { indexingStageLabel, KnowledgeIndexingBadge } from './KnowledgeIndexingBadge'
import { KnowledgeStageFailureNotice } from './KnowledgeStageFailureNotice'
import {
  documentDescription,
  documentTitle,
  facetValue,
  fileKindLabel,
  marketDisplayName,
} from './libraryHelpers'

interface KnowledgeLibraryListViewProps {
  documents: Array<KnowledgeDocumentWithStages>
  onDocumentUpdated?: (doc: KnowledgeDocumentWithStages) => void
  /** Opens the document in the library's overlay pane instead of navigating to a new page. */
  onOpen?: (token: string) => void
}

export function KnowledgeLibraryListView({
  documents,
  onDocumentUpdated,
  onOpen,
}: KnowledgeLibraryListViewProps) {
  const { t } = useTranslation('app')
  return (
    <div
      className="overflow-hidden rounded-2xl border border-border bg-card-inset dark:bg-card"
      data-testid="knowledge-list-view"
    >
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40 text-left text-muted-foreground">
          <tr>
            <th className="px-4 py-3 font-medium">{t('library.colTitle')}</th>
            <th className="hidden px-4 py-3 font-medium sm:table-cell">{t('library.colKind')}</th>
            <th className="hidden px-4 py-3 font-medium md:table-cell">{t('library.colMarket')}</th>
            <th className="hidden px-4 py-3 font-medium md:table-cell">{t('library.colYear')}</th>
            <th className="px-4 py-3 font-medium">{t('library.colStatus')}</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => {
            const description = documentDescription(doc)
            const inProgress = isContextMaterialIndexingInProgress(
              doc.indexingStatus,
            )
            const market = facetValue(doc.facets, 'market')
            return (
            <tr
              key={doc.token}
              className="border-b border-border last:border-0 hover:bg-muted/30"
              data-testid={`knowledge-list-row-${doc.token}`}
            >
              <td className="px-4 py-3">
                {inProgress ? (
                  <span
                    aria-disabled="true"
                    title={t('library.opensWhenReady', {
                      stage: indexingStageLabel(doc.indexingStatus),
                    })}
                    className="cursor-not-allowed font-serif text-base leading-snug text-muted-foreground"
                    data-testid={`knowledge-list-link-${doc.token}`}
                  >
                    {documentTitle(doc)}
                  </span>
                ) : onOpen ? (
                  <button
                    type="button"
                    className="text-left font-serif text-base leading-snug hover:underline"
                    data-testid={`knowledge-list-link-${doc.token}`}
                    onClick={() => onOpen(doc.token)}
                  >
                    {documentTitle(doc)}
                  </button>
                ) : (
                  <Link
                    href={`/knowledge/${doc.token}`}
                    className="font-serif text-base leading-snug hover:underline"
                    data-testid={`knowledge-list-link-${doc.token}`}
                  >
                    {documentTitle(doc)}
                  </Link>
                )}
                {description ? (
                  <Text size="xs" color="muted" className="mt-0.5 line-clamp-1">
                    {description}
                  </Text>
                ) : null}
                <Text size="xs" color="muted" className="mt-0.5 sm:hidden">
                  {[
                    fileKindLabel(doc),
                    market ? marketDisplayName(market) : null,
                    facetValue(doc.facets, 'year'),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </td>
              <td className="hidden px-4 py-3 text-muted-foreground sm:table-cell">
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  {fileKindLabel(doc)}
                  {doc.isSimulationArtifact ? (
                    <span
                      className="rounded-full bg-muted px-1.5 py-0.5 text-[10px]"
                      data-testid="knowledge-list-sim-badge"
                    >
                      {t('library.simBadge')}
                    </span>
                  ) : null}
                  {doc.isScannedPdf ? (
                    <span
                      className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-800 dark:text-amber-300"
                      data-testid="knowledge-list-scanned-badge"
                    >
                      {t('library.scanned')}
                    </span>
                  ) : null}
                </span>
              </td>
              <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">
                {market ? marketDisplayName(market) : '—'}
              </td>
              <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">
                {facetValue(doc.facets, 'year') || '—'}
              </td>
              <td className="px-4 py-3">
                <KnowledgeIndexingBadge status={doc.indexingStatus} />
                <KnowledgeStageFailureNotice
                  doc={doc}
                  variant="compact"
                  className="mt-1.5"
                  onDocumentUpdated={onDocumentUpdated}
                />
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
